import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {CanvasComponent} from './canvas/canvas.component';
import {ToolbarComponent} from './toolbar/toolbar.component';
import {AnnotationLegendComponent} from './annotation-legend/annotation-legend.component';
import {DocumentClient, DocumentDto, ImageAnnotation, PetClient, TupleOfstringAndstring} from '../../../api/api-client';
import {PictureAnnotationWidgetService} from '../../../services/picture-annotation-widget.service';
import {TranslationPipe} from '../../../pipes/translation.pipe';
import {AddDocumentRoutingInfo} from '../../../models/add-document-routing-info';
import {Router} from '@angular/router';
import {CardModule} from 'primeng/card';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom, Observable, of, switchMap} from 'rxjs';
import {SubscriptionManager} from '../../../services/subscription-manager.service';


@Component({
  selector: 'app-picture-annotation-widget',
  imports: [
    CanvasComponent,
    ToolbarComponent,
    AnnotationLegendComponent,
    TranslationPipe,
    CardModule,
  ],
  providers: [SubscriptionManager],
  templateUrl: './picture-annotation-widget.component.html',
  styleUrl: './picture-annotation-widget.component.scss'
})
export class PictureAnnotationWidgetComponent implements OnInit {

  @ViewChild(ToolbarComponent) toolbar!: ToolbarComponent;
  @ViewChild(CanvasComponent) canvas!: CanvasComponent;

  @Input() imageData!: DocumentDto;
  @Input() routingData!: AddDocumentRoutingInfo;

  base64ImageString!: string;

  imageLoaded: boolean = false;

  currentColor: string = 'black';
  currentDrawingMode!: "free" | "circle";
  currentCircleRadius!: number;
  currentLineWidth!: number;
  uniqueColors!: string[];
  colorUsages!: Map<string, number>;
  historyIndex: number = -42;
  canvasHistoryLength: number = -42;

  constructor(
    private readonly subscriptionManager:SubscriptionManager,
    private readonly pawService: PictureAnnotationWidgetService,
    private readonly docService: DocumentClient,
    private readonly petService:PetClient,
    private readonly router: Router,
    private readonly http: HttpClient
  ) {
    this.uniqueColors = [];
    this.colorUsages = new Map<string, number>();
    this.pawService.imageAnnotations = [];
    this.pawService.legendEntries = [];

  }

  async ngOnInit() {
    if (this.imageData?.annotation?.legendElements) {
      this.pawService.legendEntries = [...this.imageData.annotation.legendElements]
    }
    if(this.routingData.isSkeleton){
      this.base64ImageString = await this.loadImageAndConvertToBase64(`img/skeleton_${this.routingData?.species?.toLowerCase()}.svg`);
    }
    else{
      this.base64ImageString = await firstValueFrom(of(`data:${this.imageData.mimeType};base64,${this.imageData.base64Data}`));
    }
    this.imageLoaded=true;
  }

  changeColor(color: string) {
    this.currentColor = color;
  }

  loadImageAndConvertToBase64(url: string): Promise<string> {
    return firstValueFrom(this.http.get(url, {responseType: 'blob'}).pipe(
      switchMap(blob => {
        return new Observable<string>(observer => {
          const reader = new FileReader();
          reader.onload = () => {
            observer.next(reader.result as string);
            observer.complete();
          };
          reader.onerror = error => {
            observer.error(error);
          };
          reader.readAsDataURL(blob);
        });
      })
    ));
  }

  changeDrawingMode($event: "free" | "circle") {
    this.currentDrawingMode = $event;
  }

  changeLineWidth($event: number) {
    this.currentLineWidth = $event;
  }

  changeCircleRadius($event: number) {
    this.currentCircleRadius = $event;
  }

  undo() {
    this.canvas.undo();
  }

  redo() {
    this.canvas.redo();
  }

  redoneWithColor(color: string) {
    if (this.colorUsages.has(color)) {
      this.colorUsages.set(color, (this.colorUsages.get(color) ?? 0) + 1);
    }
    if ((this.colorUsages.get(color) ?? 0) == 1) {
      this.uniqueColors = [...this.uniqueColors, color]
      if (!this.pawService.legendEntries.some(x => x.x == color))
        this.pawService.legendEntries.push({x: color, y: ""} as TupleOfstringAndstring)
    }
  }

  drawnWithColor(color: string) {
    if (!this.uniqueColors.includes(color)) {
      this.uniqueColors = [...this.uniqueColors, color]
      if (!this.pawService.legendEntries.some(x => x.x == color))
        this.pawService.legendEntries.push({x: color, y: ""} as TupleOfstringAndstring)
    }
    if (this.colorUsages.has(color)) {
      this.colorUsages.set(color, (this.colorUsages.get(color) ?? 0) + 1);
    } else {
      this.colorUsages.set(color, 1);
    }
  }

  undoneWithColor(color: string) {
    if (this.colorUsages.has(color)) {
      this.colorUsages.set(color, (this.colorUsages.get(color) ?? 0) - 1);
    }
    if ((this.colorUsages.get(color) ?? 0) == 0) {
      this.uniqueColors = this.uniqueColors.filter(x => x !== color);
      this.pawService.legendEntries = this.pawService.legendEntries.filter(x => x.x != color);
    }
  }

  save() {
    if(this.routingData.isSkeleton){
      this.subscriptionManager.call(
      this.petService.addSkeletonAnnotationToPet(this.routingData.entityId, {
        annotationElements: this.pawService.imageAnnotations,
        legendElements: this.pawService.legendEntries
      } as ImageAnnotation),
        (x => {
        this.router.navigate(["loggedin/" + this.routingData.sourcePage + "/" + this.routingData.entityId]);
      })
      );
    }
    else{
      this.subscriptionManager.call(
    this.docService.addAnnotationToImage(this.imageData.id ?? "", {
      annotationElements: this.pawService.imageAnnotations,
      legendElements: this.pawService.legendEntries
    } as ImageAnnotation),
        (x => {
      this.router.navigate(["loggedin/" + this.routingData.sourcePage + "/" + this.routingData.entityId]);
    })
      );
    }
  }

  protected getAnnotations() {
    if(this.routingData.isSkeleton){
      return this.routingData.skeletonAnnotation
    }
    return this.imageData.annotation;
  }

  protected getLegendElements() {
    if(this.routingData.isSkeleton){
      return this.routingData.skeletonAnnotation?.legendElements;
    }
    return this.imageData.annotation?.legendElements;
  }
}
